/**
 * `Zip: ZipApi` (W1-07b formats-package; contract formats/types.ts; formats-and-packaging.md P§2.1, P§6.1).
 *
 * Reading parses the central directory itself (fflate only inflates), so every entry can be judged the way Phira's
 * `zip` crate (Stored + Deflate only) would judge it:
 *  - methods other than Stored (0) and Deflate (8), encrypted entries, entries whose data is truncated or whose
 *    CRC-32 does not match go to `unsupported` (Phira cannot read them) and are left out of `entries`;
 *  - names that are not "enclosed" (absolute, containing NUL, or escaping the root with '..') make Phira refuse the
 *    whole archive ("invalid zip"); they are left out and reported;
 *  - names: the UTF-8 flag (bit 11) or an Info-ZIP Unicode Path field (0x7075) give UTF-8; other non-ASCII names are
 *    decoded as UTF-8 when valid, else with the legacy encoding detection, and reported, because Phira reads such
 *    names as CP437 and references to them break;
 *  - system junk (`__MACOSX/`, `.DS_Store`, `Thumbs.db`, `desktop.ini`) is dropped, directories are not files;
 *  - ZIP64 archives and data descriptors are read.
 *
 * Root folder analysis: Phira's importer strips a root folder only when the SHORTEST entry name ends with '/' and
 * every entry (junk and directories included) starts with it, i.e. one top-level folder WITH an explicit directory
 * entry. Here a common top-level folder is stripped even without a directory entry, repeatedly (`A/B/chart.json` ->
 * root `A/B`); `phiraStripsRoot` tells whether Phira's rule yields exactly the same root. `entries` are relative to
 * the stripped root.
 *
 * Writing produces what Phira reads best: a flat archive, entries in the given order, Deflate (Stored when Deflate
 * would not make the entry smaller, e.g. for compressed media), a fixed 1980-01-01 timestamp and MS-DOS "made by"
 * attributes, so equal inputs give identical bytes; the UTF-8 flag only for non-ASCII names (never produced by the
 * exporter, whose names are ASCII).
 */

import { deflateSync, inflateSync } from 'fflate';
import { guessLegacyEncoding } from '../info/encoding';
import type { ZipApi } from '../types';
import { crc32 } from './hash';

/** Thrown when the bytes are not a zip archive at all. */
export class ZipFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ZipFormatError';
  }
}

export type ZipIssue =
  | { readonly kind: 'notEnclosed'; readonly name: string }
  | { readonly kind: 'unsupportedMethod'; readonly name: string; readonly method: number }
  | { readonly kind: 'encrypted'; readonly name: string }
  | { readonly kind: 'corrupt'; readonly name: string }
  | { readonly kind: 'duplicate'; readonly name: string }
  | { readonly kind: 'nameEncoding'; readonly name: string; readonly encoding: string }
  | { readonly kind: 'junk'; readonly name: string };

export interface UnzipDetailed {
  /** Files relative to `rootFolder` (or the archive root), in central directory order. */
  readonly entries: ReadonlyMap<string, Uint8Array>;
  /** Entries Phira cannot read (method, encryption, corruption), by original name. */
  readonly unsupported: readonly string[];
  readonly rootFolder: string | null;
  readonly phiraStripsRoot: boolean;
  /** The prefix Phira's importer strips (with its trailing '/'), or null. */
  readonly phiraRootPrefix: string | null;
  readonly issues: readonly ZipIssue[];
  /** Every entry name (files and directories) in central directory order, as decoded. */
  readonly names: readonly string[];
}

const SIG_LOCAL = 0x04034b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_EOCD = 0x06054b50;
const SIG_ZIP64_LOCATOR = 0x07064b50;
const SIG_ZIP64_EOCD = 0x06064b50;
const FLAG_ENCRYPTED = 0x0001;
const FLAG_UTF8 = 0x0800;
const METHOD_STORED = 0;
const METHOD_DEFLATE = 8;
const U32_MAX = 0xffffffff;

/** True when the bytes start like a zip archive (local header, or an empty archive's end record). */
export function looksLikeZip(bytes: Uint8Array): boolean {
  if (bytes.length < 4) return false;
  const sig = (bytes[0] ?? 0) | ((bytes[1] ?? 0) << 8) | ((bytes[2] ?? 0) << 16) | ((bytes[3] ?? 0) << 24);
  return sig === SIG_LOCAL || sig === SIG_EOCD;
}

class Reader {
  readonly bytes: Uint8Array;
  private readonly view: DataView;
  constructor(bytes: Uint8Array) {
    this.bytes = bytes;
    this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  }
  get length(): number {
    return this.bytes.length;
  }
  u16(at: number): number {
    if (at + 2 > this.bytes.length) throw new ZipFormatError('truncated zip');
    return this.view.getUint16(at, true);
  }
  u32(at: number): number {
    if (at + 4 > this.bytes.length) throw new ZipFormatError('truncated zip');
    return this.view.getUint32(at, true);
  }
  u64(at: number): number {
    const lo = this.u32(at);
    const hi = this.u32(at + 4);
    return hi * 0x100000000 + lo;
  }
  slice(at: number, len: number): Uint8Array {
    if (at < 0 || len < 0 || at + len > this.bytes.length) throw new ZipFormatError('truncated zip');
    return this.bytes.subarray(at, at + len);
  }
}

interface CentralEntry {
  readonly name: string;
  readonly rawName: Uint8Array;
  readonly flags: number;
  readonly method: number;
  readonly crc: number;
  readonly compressedSize: number;
  readonly size: number;
  readonly localOffset: number;
  readonly externalAttr: number;
  readonly nameEncoding: string | null;
}

function isAsciiBytes(b: Uint8Array): boolean {
  for (const x of b) if (x >= 0x80) return false;
  return true;
}

function decodeName(
  raw: Uint8Array,
  flags: number,
  unicodePath: string | null,
): { name: string; encoding: string | null } {
  if ((flags & FLAG_UTF8) !== 0) return { name: new TextDecoder('utf-8').decode(raw), encoding: null };
  if (unicodePath !== null) return { name: unicodePath, encoding: null };
  if (isAsciiBytes(raw)) return { name: new TextDecoder('utf-8').decode(raw), encoding: null };
  try {
    return { name: new TextDecoder('utf-8', { fatal: true }).decode(raw), encoding: 'utf-8' };
  } catch {
    const g = guessLegacyEncoding(raw);
    return { name: g.text, encoding: g.encoding };
  }
}

function findEocd(r: Reader): number {
  const min = Math.max(0, r.length - 22 - 0xffff);
  for (let i = r.length - 22; i >= min; i--) {
    if (r.u32(i) === SIG_EOCD) return i;
  }
  throw new ZipFormatError('not a zip archive (no end of central directory)');
}

function readCentralDirectory(r: Reader): CentralEntry[] {
  const eocd = findEocd(r);
  let count = r.u16(eocd + 10);
  let cdSize = r.u32(eocd + 12);
  let cdOffset = r.u32(eocd + 16);
  if (count === 0xffff || cdSize === U32_MAX || cdOffset === U32_MAX) {
    const loc = eocd - 20;
    if (loc >= 0 && r.u32(loc) === SIG_ZIP64_LOCATOR) {
      const z = r.u64(loc + 8);
      if (r.u32(z) !== SIG_ZIP64_EOCD) throw new ZipFormatError('broken ZIP64 end record');
      count = r.u64(z + 32);
      cdSize = r.u64(z + 40);
      cdOffset = r.u64(z + 48);
    }
  }
  // Self-extracting or prefixed archives: offsets are relative to the archive start, which is not byte 0; the
  // directory then sits right before the end record.
  const sigAt = (at: number): number => (at >= 0 && at + 4 <= r.length ? r.u32(at) : -1);
  let shift = 0;
  if (count > 0 && sigAt(cdOffset) !== SIG_CENTRAL) {
    const alt = eocd - cdSize;
    if (sigAt(alt) !== SIG_CENTRAL) throw new ZipFormatError('broken central directory');
    shift = alt - cdOffset;
  }
  const out: CentralEntry[] = [];
  let p = cdOffset + shift;
  for (let i = 0; i < count; i++) {
    if (r.u32(p) !== SIG_CENTRAL) throw new ZipFormatError('broken central directory');
    const flags = r.u16(p + 8);
    const method = r.u16(p + 10);
    const crc = r.u32(p + 16);
    let compressedSize = r.u32(p + 20);
    let size = r.u32(p + 24);
    const nameLen = r.u16(p + 28);
    const extraLen = r.u16(p + 30);
    const commentLen = r.u16(p + 32);
    const externalAttr = r.u32(p + 38);
    let localOffset = r.u32(p + 42);
    const rawName = r.slice(p + 46, nameLen);
    const extra = r.slice(p + 46 + nameLen, extraLen);
    let unicodePath: string | null = null;
    let e = 0;
    while (e + 4 <= extra.length) {
      const id = (extra[e] ?? 0) | ((extra[e + 1] ?? 0) << 8);
      const len = (extra[e + 2] ?? 0) | ((extra[e + 3] ?? 0) << 8);
      const body = extra.subarray(e + 4, e + 4 + len);
      const bv = new Reader(body);
      if (id === 0x0001) {
        let q = 0;
        if (size === U32_MAX && q + 8 <= body.length) {
          size = bv.u64(q);
          q += 8;
        }
        if (compressedSize === U32_MAX && q + 8 <= body.length) {
          compressedSize = bv.u64(q);
          q += 8;
        }
        if (localOffset === U32_MAX && q + 8 <= body.length) localOffset = bv.u64(q);
      } else if (id === 0x7075 && body.length >= 5 && body[0] === 1 && bv.u32(1) === crc32(rawName)) {
        unicodePath = new TextDecoder('utf-8').decode(body.subarray(5));
      }
      e += 4 + len;
    }
    const decoded = decodeName(rawName, flags, unicodePath);
    out.push({
      name: decoded.name,
      rawName,
      flags,
      method,
      crc,
      compressedSize,
      size,
      localOffset: localOffset + shift,
      externalAttr,
      nameEncoding: decoded.encoding,
    });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/** Phira's "enclosed name": no NUL, not absolute, never above the root. Returns the normalised path or null. */
export function enclosedName(name: string): string | null {
  if (name.includes('\0')) return null;
  if (name.startsWith('/') || /^[A-Za-z]:/.test(name)) return null;
  const out: string[] = [];
  for (const seg of name.split('/')) {
    if (seg === '' || seg === '.') continue;
    if (seg === '..') {
      if (out.length === 0) return null;
      out.pop();
      continue;
    }
    out.push(seg);
  }
  return out.join('/');
}

const JUNK_FILES: ReadonlySet<string> = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

/** OS metadata that is not part of a package (macOS resource forks, Finder / Explorer files). */
export function isJunkPath(path: string): boolean {
  if (path === '__MACOSX' || path.startsWith('__MACOSX/')) return true;
  const base = path.slice(path.lastIndexOf('/') + 1);
  return JUNK_FILES.has(base) || base.startsWith('._');
}

function isDirectoryEntry(e: CentralEntry): boolean {
  return e.name.endsWith('/') || (e.size === 0 && e.compressedSize === 0 && (e.externalAttr & 0x10) !== 0);
}

/** Phira's importer root prefix: the shortest name, when it ends with '/' and prefixes every name (P§2.1). */
export function phiraRootPrefix(names: readonly string[]): string | null {
  let shortest: string | undefined;
  for (const n of names) if (shortest === undefined || n.length < shortest.length) shortest = n;
  if (shortest?.endsWith('/') !== true) return null;
  const prefix = shortest;
  return names.every((n) => n.startsWith(prefix)) ? prefix : null;
}

/** Common top-level folder(s) of a set of file paths ("A/B" for files all under A/B/), or null. */
export function commonRootFolder(paths: readonly string[]): string | null {
  if (paths.length === 0) return null;
  const split = paths.map((p) => p.split('/'));
  const root: string[] = [];
  for (;;) {
    const depth = root.length;
    const first = split[0]?.[depth];
    if (first === undefined) break;
    if (!split.every((s) => s.length > depth + 1 && s[depth] === first)) break;
    root.push(first);
  }
  return root.length === 0 ? null : root.join('/');
}

function readEntryData(r: Reader, e: CentralEntry): Uint8Array | 'corrupt' {
  if (r.u32(e.localOffset) !== SIG_LOCAL) return 'corrupt';
  const nameLen = r.u16(e.localOffset + 26);
  const extraLen = r.u16(e.localOffset + 28);
  const start = e.localOffset + 30 + nameLen + extraLen;
  if (start + e.compressedSize > r.length) return 'corrupt';
  const data = r.slice(start, e.compressedSize);
  let out: Uint8Array;
  try {
    out = e.method === METHOD_STORED ? data : inflateSync(data, { out: new Uint8Array(e.size) });
  } catch {
    return 'corrupt';
  }
  if (out.length !== e.size || crc32(out) !== e.crc) return 'corrupt';
  return out;
}

/**
 * Full unzip with the issue list the importer reports (see the module header). `onEntry` (CR-9) is called with
 * each package path as it is unpacked, so an importer can show "which file" in a progress label.
 */
export function unzipDetailed(bytes: Uint8Array, onEntry?: (path: string) => void): UnzipDetailed {
  const r = new Reader(bytes);
  const central = readCentralDirectory(r);
  const issues: ZipIssue[] = [];
  const unsupported: string[] = [];
  const names = central.map((e) => e.name);
  const files: Array<{ readonly path: string; readonly data: Uint8Array }> = [];
  const seen = new Set<string>();
  for (const e of central) {
    if (isDirectoryEntry(e)) continue;
    const path = enclosedName(e.name);
    if (path === null || path === '') {
      issues.push({ kind: 'notEnclosed', name: e.name });
      continue;
    }
    if (isJunkPath(path)) {
      issues.push({ kind: 'junk', name: e.name });
      continue;
    }
    if ((e.flags & FLAG_ENCRYPTED) !== 0) {
      issues.push({ kind: 'encrypted', name: e.name });
      unsupported.push(e.name);
      continue;
    }
    if (e.method !== METHOD_STORED && e.method !== METHOD_DEFLATE) {
      issues.push({ kind: 'unsupportedMethod', name: e.name, method: e.method });
      unsupported.push(e.name);
      continue;
    }
    onEntry?.(path);
    const data = readEntryData(r, e);
    if (data === 'corrupt') {
      issues.push({ kind: 'corrupt', name: e.name });
      unsupported.push(e.name);
      continue;
    }
    if (e.nameEncoding !== null) issues.push({ kind: 'nameEncoding', name: e.name, encoding: e.nameEncoding });
    if (seen.has(path)) {
      issues.push({ kind: 'duplicate', name: e.name });
      const at = files.findIndex((f) => f.path === path);
      if (at >= 0) files.splice(at, 1);
    }
    seen.add(path);
    files.push({ path, data });
  }
  const rootFolder = commonRootFolder(files.map((f) => f.path));
  const prefix = phiraRootPrefix(names);
  const strip = rootFolder === null ? 0 : rootFolder.length + 1;
  const entries = new Map<string, Uint8Array>();
  for (const f of files) entries.set(f.path.slice(strip), f.data);
  return {
    entries,
    unsupported,
    rootFolder,
    phiraStripsRoot: rootFolder !== null && prefix === `${rootFolder}/`,
    phiraRootPrefix: prefix,
    issues,
    names,
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Writer
// ---------------------------------------------------------------------------------------------------------------

/** MS-DOS date 1980-01-01 (time 00:00:00): the fixed timestamp of every written entry. */
const DOS_DATE = (0 << 9) | (1 << 5) | 1;
const DOS_TIME = 0;

function isAsciiName(name: string): boolean {
  for (let i = 0; i < name.length; i++) if (name.charCodeAt(i) >= 0x80) return false;
  return true;
}

/** A flat, deterministic archive (see the module header). Throws on invalid or duplicate names (a bug). */
export function zipSyncDeterministic(
  files: ReadonlyArray<{ readonly path: string; readonly bytes: Uint8Array }>,
): Uint8Array {
  const enc = new TextEncoder();
  const seen = new Set<string>();
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const f of files) {
    if (enclosedName(f.path) !== f.path || f.path === '' || f.path.includes('\\')) {
      throw new Error(`zip: invalid entry name ${JSON.stringify(f.path)}`);
    }
    if (seen.has(f.path)) throw new Error(`zip: duplicate entry ${JSON.stringify(f.path)}`);
    seen.add(f.path);
    if (f.bytes.length >= U32_MAX || offset >= U32_MAX || files.length >= 0xffff) {
      throw new Error('zip: archive too large for a non-ZIP64 zip');
    }
    const name = enc.encode(f.path);
    const flags = isAsciiName(f.path) ? 0 : FLAG_UTF8;
    const deflated = deflateSync(f.bytes, { level: 6 });
    const method = deflated.length < f.bytes.length ? METHOD_DEFLATE : METHOD_STORED;
    const data = method === METHOD_DEFLATE ? deflated : f.bytes;
    const crc = crc32(f.bytes);

    const local = new Uint8Array(30 + name.length);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, SIG_LOCAL, true);
    lv.setUint16(4, 20, true);
    lv.setUint16(6, flags, true);
    lv.setUint16(8, method, true);
    lv.setUint16(10, DOS_TIME, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, data.length, true);
    lv.setUint32(22, f.bytes.length, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);

    const cd = new Uint8Array(46 + name.length);
    const cv = new DataView(cd.buffer);
    cv.setUint32(0, SIG_CENTRAL, true);
    cv.setUint16(4, 20, true);
    cv.setUint16(6, 20, true);
    cv.setUint16(8, flags, true);
    cv.setUint16(10, method, true);
    cv.setUint16(12, DOS_TIME, true);
    cv.setUint16(14, DOS_DATE, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, data.length, true);
    cv.setUint32(24, f.bytes.length, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    cd.set(name, 46);

    parts.push(local, data);
    central.push(cd);
    offset += local.length + data.length;
  }
  const cdSize = central.reduce((s, c) => s + c.length, 0);
  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, SIG_EOCD, true);
  ev.setUint16(8, files.length, true);
  ev.setUint16(10, files.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  const out = new Uint8Array(offset + cdSize + eocd.length);
  let p = 0;
  for (const part of [...parts, ...central, eocd]) {
    out.set(part, p);
    p += part.length;
  }
  return out;
}

export const Zip: ZipApi = {
  unzip: (bytes) => {
    try {
      const d = unzipDetailed(bytes);
      return Promise.resolve({
        entries: d.entries,
        unsupported: d.unsupported,
        rootFolder: d.rootFolder,
        phiraStripsRoot: d.phiraStripsRoot,
      });
    } catch (e) {
      return Promise.reject(e instanceof Error ? e : new ZipFormatError(String(e)));
    }
  },
  zip: (files) => {
    try {
      return Promise.resolve(zipSyncDeterministic(files));
    } catch (e) {
      return Promise.reject(e instanceof Error ? e : new Error(String(e)));
    }
  },
};
